import React, { FC, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { ProductionValueTemplate, ProductionValueTemplateStepInput } from 'models/organization';
import { useValueTemplate } from 'hooks/useValueTemplate';
import ValueTemplateSteps from './value-template-steps/value-template-steps.component';

type ValueTemplateStep = {
    _id: string;
    name: string;
    valuePercent: number | '';
};

type AddEditValueTemplateDialogProps = {
    open: boolean;
    productionTemplate?: ProductionValueTemplate;
    onDialogClose: () => void;
};

const AddEditValueTemplateDialog: FC<AddEditValueTemplateDialogProps> = ({
    open,
    productionTemplate,
    onDialogClose,
}) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { addValueTemplate, editValueTemplate, addTemplateLoading, editTemplateLoading } = useValueTemplate();

    const [name, setName] = useState('');
    const [steps, setSteps] = useState<ValueTemplateStep[]>([]);
    const [isStepsValid, setIsStepsValid] = useState(true);

    useEffect(() => {
        if (!open) return;
        const initialSteps: ValueTemplateStep[] = (productionTemplate?.steps || []).map((step) => ({
            _id: step.stepId || step._id || '',
            name: step.name ?? '',
            valuePercent: step.valuePercent ?? 0,
        }));
        const initialTotalPercent = initialSteps.reduce(
            (totalSum, step) => totalSum + (Number(step.valuePercent) || 0),
            0,
        );
        setName(productionTemplate?.name || '');
        setSteps(initialSteps);
        setIsStepsValid(initialSteps.length === 0 || initialTotalPercent === 100);
    }, [open, productionTemplate]);

    const handleStepsChange = useCallback((updatedSteps: ValueTemplateStep[], isValid: boolean) => {
        setSteps(updatedSteps);
        setIsStepsValid(isValid);
    }, []);

    const isSubmitDisabled = useMemo(
        () => !name.trim() || steps.length === 0 || !isStepsValid,
        [name, steps.length, isStepsValid],
    );

    const handleSubmit = useCallback(async () => {
        try {
            const templateData = {
                name,
                steps: steps.map(
                    (step, index): ProductionValueTemplateStepInput => ({
                        stepId: step._id,
                        stepOrder: index + 1,
                        valuePercent: Number(step.valuePercent) || 0,
                    }),
                ),
            };

            if (productionTemplate?._id) {
                await editValueTemplate({ template: { ...templateData, templateId: productionTemplate._id } });
            } else {
                await addValueTemplate({ template: templateData });
            }

            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: productionTemplate?._id
                    ? translate('common:CHANGES_SAVED')
                    : translate('lab-settings:PRODUCTION_VALUE_TEMPLATE_ADDED_SUCCESSFULLY'),
            });

            onDialogClose();
        } catch (error) {
            console.error('Error saving value template:', error);
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('common:ERROR_OCCURRED'),
            });
        }
    }, [
        name,
        steps,
        productionTemplate,
        addValueTemplate,
        editValueTemplate,
        setAppToasterContent,
        translate,
        onDialogClose,
    ]);

    return (
        <DHDialog
            title={
                productionTemplate?._id
                    ? translate('lab-settings:EDIT_PRODUCTION_VALUE_TEMPLATE')
                    : translate('lab-settings:ADD_PRODUCTION_VALUE_TEMPLATE')
            }
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={onDialogClose}
            height="450px"
            actions={
                <LoadingButton
                    disabled={isSubmitDisabled}
                    variant="contained"
                    type="submit"
                    loading={productionTemplate?._id ? editTemplateLoading : addTemplateLoading}
                    onClick={handleSubmit}
                >
                    {productionTemplate?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </LoadingButton>
            }
        >
            <Box mt={1}>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={translate('lab-settings:PRODUCTION_VALUE_TEMPLATE_NAME')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        variant="outlined"
                        size="small"
                        required
                    />
                </Box>
                <ValueTemplateSteps productionTemplate={productionTemplate ?? null} onStepsChange={handleStepsChange} />
            </Box>
        </DHDialog>
    );
};

export default AddEditValueTemplateDialog;
